import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import {
  DeleteRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  DocumentTableRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { listCatalogs, deleteCatalog } from '../api/catalogs.js';
import { ApiClientError } from '../api/client.js';
import type { Catalog } from '@supernova/shared';
import { puedeVerDefectosDelCatalogo } from '@supernova/shared';
import { useIsAdmin, useIsDemoGuest, useIsEcosoftStaff } from '../stores/authStore.js';
import { useConfirm } from '../components/ConfirmDialog.js';
import {
  catalogsForDemoPage,
  isDemoDisplayOnlyCatalogId,
} from '../demo/demoCatalogLabels.js';
import type { ComponentType, SVGProps } from 'react';
import * as Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';

// Banderas: SVG reales por código ISO (país). No usamos emoji porque Windows no
// tiene fuente de banderas y los pinta como texto ("MX"). La librería cubre
// todos los países por su código ISO — no es una tabla hardcodeada.
const FLAG_COMPONENTS = Flags as unknown as Record<
  string,
  ComponentType<SVGProps<SVGSVGElement> & { title?: string }>
>;
// Nombre del país desde el código ISO vía la plataforma (Intl.DisplayNames).
const regionNames = (() => {
  try { return new Intl.DisplayNames(['es'], { type: 'region' }); } catch { return null; }
})();
function countryLabel(cc: string | null): string {
  if (!cc) return '';
  try { return regionNames?.of(cc.toUpperCase()) ?? cc.toUpperCase(); } catch { return cc.toUpperCase(); }
}
/** Componente de bandera SVG para un código ISO; null si no hay país/bandera. */
function FlagIcon({ code, className }: { code: string | null; className?: string }) {
  const cc = code?.toUpperCase();
  if (!cc || !hasFlag(cc)) return null;
  const Comp = FLAG_COMPONENTS[cc];
  return Comp ? <Comp className={className} title={countryLabel(cc)} /> : null;
}
/** Leyenda "Colombia · COP" para catálogos de referencia con país. */
function catalogLegend(cat: Catalog): string {
  if (!cat.country) return '';
  const name = countryLabel(cat.country);
  const cur = cat.currency ? ` · ${cat.currency}` : '';
  return `${name}${cur}`;
}

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-page)' },
  container: { maxWidth: '1000px', margin: '0 auto', padding: '24px 28px' },
  pageHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', marginBottom: '20px' },
  title: { fontFamily: 'var(--font-display)', fontSize: '23px', fontWeight: 700, letterSpacing: '-0.3px', margin: 0, display: 'block', lineHeight: 1.2 },
  subtitle: { color: 'var(--text-secondary)', fontSize: '13px', marginTop: '5px', display: 'block', lineHeight: 1.5 },
  panel: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    padding: '18px 20px',
    marginBottom: '16px',
  },
  panelHeadRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  eyebrow: { fontSize: '11px', fontWeight: 700, letterSpacing: '1.1px', textTransform: 'uppercase', color: 'var(--text-tertiary)' },
  panelDesc: { fontSize: '12.5px', color: 'var(--text-secondary)', margin: '6px 0 14px', lineHeight: 1.45 },
  catItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    marginBottom: '8px',
    background: 'var(--bg-raised)',
  },
  catItemOficial: { border: '1px solid var(--oficial)', background: 'var(--oficial-weak)' },
  catIcon: { color: 'var(--text-tertiary)', flexShrink: 0, fontSize: '18px' },
  catFlag: { flexShrink: 0, width: '26px', height: 'auto', borderRadius: '3px', boxShadow: '0 0 0 1px var(--border-subtle)' },
  catInfo: { minWidth: 0, flex: 1 },
  catName: { fontSize: '13px', fontWeight: 650, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  catSub: { fontSize: '11.5px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' },
  catAviso: { fontSize: '11.5px', color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '3px' },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '23px',
    padding: '0 9px',
    borderRadius: '999px',
    fontSize: '11.5px',
    fontWeight: 650,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  badgeOficial: { background: 'var(--oficial-weak)', color: 'var(--oficial)' },
  badgePrivado: { background: 'var(--privado-weak)', color: 'var(--privado)' },
  badgeMute: { background: 'var(--bg-overlay)', color: 'var(--text-tertiary)' },
  badgeDot: { width: '7px', height: '7px', borderRadius: '50%', background: 'currentColor', flexShrink: 0 },
  trustbox: {
    marginTop: '12px',
    marginBottom: '12px',
    border: '1px solid var(--oficial)',
    background: 'var(--oficial-weak)',
    borderRadius: 'var(--radius-md)',
    padding: '11px 13px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    lineHeight: 1.45,
  },
  empty: { color: 'var(--text-tertiary)', fontSize: '13px', padding: '8px 2px' },
});

/**
 * Un catálogo está "generándose" mientras se sube/parsea O mientras corren los
 * embeddings en background (status ya es 'ready' pero faltan compuestos por
 * embedear). Devuelve el % [0..100] si se puede calcular, o `true` (sin %), o
 * `false` si ya está completo.
 */
function generationProgress(cat: Catalog): number | boolean {
  if (cat.status === 'failed' || cat.status === 'parsing' || cat.status === 'uploading' || cat.status === 'syncing') {
    return true; // generando, sin % aún
  }
  // status === 'ready': revisar embeddings
  const p = cat.embeddingProgress;
  if (p && p.total > 0 && p.done < p.total) {
    return Math.min(99, Math.round((p.done / p.total) * 100));
  }
  return false; // listo del todo
}

function isGenerating(cat: Catalog): boolean {
  return generationProgress(cat) !== false;
}

function StatusIcon({ cat }: { cat: Catalog }) {
  if (cat.status === 'failed') return <ErrorCircleRegular style={{ color: 'var(--bad)' }} />;
  if (isGenerating(cat)) return <Spinner size="tiny" />;
  return <CheckmarkCircleRegular style={{ color: 'var(--ok)' }} />;
}

/**
 * El aviso de lo que entró y no se va a poder usar.
 *
 * Se muestra al lado del conteo de recursos, en el momento en que uno mira su
 * catálogo, porque el resto del tiempo no hay dónde enterarse: los renglones sin
 * unidad y los que vienen en cero se ven idénticos a los buenos en la tabla, y el
 * problema recién aparece semanas después, adentro de un presupuesto.
 *
 * QUIÉN LO VE. El de TU catálogo lo ves vos, porque es tuyo y lo podés arreglar: es
 * el aviso al subirlo que pidió Sebastián. El de los catálogos oficiales (ECOSTOS y
 * los de referencia por país) lo ve solo Ecosoft, que es quien los mantiene —
 * anunciarle al cliente los defectos de nuestra propia base no le sirve para nada y
 * le resta confianza en un precio que igual no vamos a usar. La regla está en
 * `puedeVerDefectosDelCatalogo` (shared) y el API la aplica igual: cuando no
 * corresponde, los números NI VIAJAN (`resourcesSinUnidad` llega ausente).
 *
 * No dice "renglones inservibles" ni nombra columnas de base de datos: dice qué le
 * falta a cada uno y qué consecuencia tiene.
 */
function AvisoRenglonesInservibles({ cat, verlosTodos }: { cat: Catalog; verlosTodos: boolean }) {
  const styles = useStyles();
  const sinUnidad = cat.resourcesSinUnidad ?? 0;
  const sinPrecio = cat.resourcesSinPrecio ?? 0;
  if (cat.status !== 'ready' || (sinUnidad === 0 && sinPrecio === 0)) return null;
  if (!puedeVerDefectosDelCatalogo(cat.kind, verlosTodos)) return null;
  const partes: string[] = [];
  if (sinUnidad > 0) {
    partes.push(
      sinUnidad === 1
        ? '1 no dice en qué se mide'
        : `${sinUnidad.toLocaleString()} no dicen en qué se miden`,
    );
  }
  if (sinPrecio > 0) {
    partes.push(sinPrecio === 1 ? '1 viene sin precio' : `${sinPrecio.toLocaleString()} vienen sin precio`);
  }
  return (
    <div className={styles.catAviso} title="Estos recursos quedan fuera al armar un presupuesto.">
      <WarningRegular />
      <span>De este catálogo, {partes.join(' y ')}. No se usan para poner precios.</span>
    </div>
  );
}

function StatusLabel({ cat }: { cat: Catalog }) {
  if (cat.status === 'failed') {
    // El motivo del fallo se guardaba desde siempre y no se mostraba nunca: la
    // pantalla decía "Error" y no había forma de saber qué arreglar del archivo.
    return <>{cat.lastSyncError?.trim() ? cat.lastSyncError : 'Error'}</>;
  }
  const prog = generationProgress(cat);
  if (prog === false) return <>Listo</>;
  // Generando: en esta versión los precios no son el foco, así que el mensaje
  // habla del catálogo, no de "indexar precios".
  if (typeof prog === 'number') return <>Generando catálogo… {prog}%</>;
  return <>Generando catálogo…</>;
}

export function CatalogsPage() {
  const styles = useStyles();
  const confirm = useConfirm();
  const isAdmin = useIsAdmin();
  // Ecosoft ve además los defectos de los catálogos oficiales: es quien los corrige.
  const isEcosoftStaff = useIsEcosoftStaff();
  const isDemo = useIsDemoGuest();
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async () => {
      try {
        const data = await listCatalogs();
        if (cancelled) return;
        setCatalogs(data);
        setError(null);
        // Mientras algún catálogo se esté generando (parseo o embeddings en
        // background), refrescar para que el % avance solo.
        if (!isDemo && data.some(isGenerating)) {
          timer = setTimeout(load, 3000);
        }
      } catch (e) {
        if (cancelled) return;
        // En demo la página puede mostrarse con datos de muestra aunque falle la API.
        if (isDemo) {
          setCatalogs([]);
          setError(null);
        } else {
          setError(e instanceof ApiClientError ? e.message : 'Error cargando catálogos');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isDemo]);

  async function handleDelete(id: string) {
    if (!(await confirm({
      title: 'Eliminar catálogo',
      message: '¿Eliminar este catálogo? Se quitará de todos los proyectos.',
      confirmLabel: 'Eliminar',
      destructive: true,
    }))) return;
    setDeleting(id);
    try {
      await deleteCatalog(id);
      setCatalogs((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error al eliminar');
    } finally {
      setDeleting(null);
    }
  }

  // Catálogos de referencia (públicos, Ecosoft): ECOSTOS (México) + los de otros
  // países. Los privados de la organización van aparte.
  // En demo: banderas de América + privados inventados por tipo de obra (solo UI).
  const { referencia, propios } = isDemo
    ? catalogsForDemoPage(catalogs)
    : {
        referencia: catalogs.filter((c) => c.kind === 'ecostos' || c.kind === 'referencia'),
        propios: catalogs.filter((c) => c.kind === 'tenant_upload'),
      };

  const CatRow = ({ cat }: { cat: Catalog }) => {
    const oficial = cat.kind === 'ecostos' || cat.kind === 'referencia';
    const legend = catalogLegend(cat);
    const hasFlagIcon = !!cat.country && hasFlag(cat.country.toUpperCase());
    const displayOnly = isDemo && isDemoDisplayOnlyCatalogId(cat.id);
    const label = (cat.displayName?.trim() || cat.name);
    const nameTitle = cat.displayName?.trim() && cat.displayName.trim() !== cat.name
      ? `${label} (obra/clave: ${cat.name})`
      : label;
    const nameEl = displayOnly ? (
      <span className={styles.catName} title={`${nameTitle} (vista de demostración)`} style={{ color: 'var(--accent)' }}>{label}</span>
    ) : (
      <Link to={`/catalogs/${cat.id}`} className={styles.catName} title={`Ver contenido de ${nameTitle}`} style={{ textDecoration: 'none', color: 'var(--accent)' }}>{label}</Link>
    );
    return (
      <div className={`${styles.catItem}${oficial ? ` ${styles.catItemOficial}` : ''}`}>
        {hasFlagIcon ? (
          <FlagIcon code={cat.country} className={styles.catFlag} />
        ) : (
          <DocumentTableRegular className={styles.catIcon} />
        )}
        <div className={styles.catInfo}>
          {nameEl}
          <div className={styles.catSub}>
            <StatusIcon cat={cat} />
            <StatusLabel cat={cat} />
            {' · '}{cat.resourcesCount.toLocaleString()} recursos
            {cat.compuestosCount > 0 && ` · ${cat.compuestosCount.toLocaleString()} matrices`}
          </div>
          <AvisoRenglonesInservibles cat={cat} verlosTodos={isEcosoftStaff} />
        </div>
        {oficial ? (
          <span className={`${styles.badge} ${styles.badgeOficial}`} title={legend ? `Catálogo de ${countryLabel(cat.country)}${cat.currency ? ` · precios en ${cat.currency}` : ''}` : undefined}>
            <span className={styles.badgeDot} />{legend || 'Oficial Ecosoft'}
          </span>
        ) : (
          <>
            <span className={`${styles.badge} ${styles.badgePrivado}`}><span className={styles.badgeDot} />Privado · tu organización</span>
            {isAdmin && !isDemo && (
              <Button size="small" appearance="subtle" icon={<DeleteRegular />} disabled={deleting === cat.id} onClick={() => handleDelete(cat.id)}>
                Eliminar
              </Button>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Catálogos de referencia' }]} />
      <div className={styles.container}>
        <div className={styles.pageHead}>
          <div>
            <Text as="h1" className={styles.title}>Catálogos</Text>
            <Text className={styles.subtitle}>
              eCostos (base oficial de Ecosoft) siempre incluido. Tus catálogos propios cuentan en el límite del plan.
            </Text>
          </div>
        </div>

        {loading && <Spinner label="Cargando catálogos…" />}
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {!loading && !error && (
          <>
            {/* Catálogos de referencia — públicos, por país (ECOSTOS = México) */}
            <div className={styles.panel}>
              <div className={styles.eyebrow}>eCostos y bases oficiales</div>
              <p className={styles.panelDesc}>
                eCostos siempre incluido — no cuenta aquí. Es la base oficial de precios de Ecosoft (y equivalentes por país).
                Tus catálogos propios del usuario/organización sí consumen el límite del plan.
              </p>
              {referencia.length === 0 ? (
                <Text className={styles.empty}>Aún no se ha sincronizado ECOSTOS. Se genera al publicar una obra desde OPUS 27.</Text>
              ) : (
                referencia.map((cat) => <CatRow key={cat.id} cat={cat} />)
              )}
            </div>

            {/* Catálogos propios de la organización (privado) */}
            <div className={styles.panel}>
              <div className={styles.panelHeadRow}>
                <div className={styles.eyebrow}>Catálogos propios de tu organización</div>
                <span className={`${styles.badge} ${styles.badgeMute}`}>
                  <span className={styles.badgeDot} />{propios.length} {propios.length === 1 ? 'activo' : 'activos'}
                </span>
              </div>
              <div className={styles.trustbox}>
                <CheckmarkCircleRegular style={{ color: 'var(--oficial)', fontSize: 17, flexShrink: 0, marginTop: 1 }} />
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>Privados de tu organización.</strong> Los cargas tú, quedan aislados en tu espacio y nadie más puede verlos ni usarlos.
                </div>
              </div>
              {propios.length === 0 ? (
                <Text className={styles.empty}>No tienes catálogos propios. Se agregan al exportar una obra desde OPUS 27.</Text>
              ) : (
                propios.map((cat) => <CatRow key={cat.id} cat={cat} />)
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
